import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useAppDispatch } from "app/hooks";
import { searchUsersAsync } from "app/services/users/userSlice";
import { Drawer } from "components/Drawer";
import DataGrid from "components/table/DataGrid";
import { ROUTES } from "definitions/constant/routes";
import { useLocation } from "react-router-dom";
import { UserForm } from "./UserForm";
import {
  Box, Typography, Button, alpha, useTheme, useMediaQuery,
  Stack, Chip, CircularProgress,
} from "@mui/material";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PersonAddIcon from "@mui/icons-material/PersonAdd";

export const UserCom = () => {
  const { pathname } = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useAppDispatch();

  const [openDrawer, setOpenDrawer] = useState(false);
  const [readonly, setReadOnly] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [list, setList] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "name", headerName: "Name", flex: 1 },
      { field: "email", headerName: "Email", flex: 1 },
      { field: "mobile", headerName: "Mobile", flex: 1 },
      { field: "active", headerName: "Active", flex: 1 },
      { field: "role", headerName: "Role", flex: 1 },
      { field: "city", headerName: "Location", flex: 1 },
    ],
    []
  );

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      const asyncFunc = async () => {
        try {
          const res = await dispatch(searchUsersAsync());
          if (
            res.payload &&
            typeof res.payload === "object" &&
            "data" in res.payload &&
            Array.isArray(res.payload.data)
          ) {
            setList(res.payload.data.map((item: any) => ({ ...item })));
          }
        } finally {
          setLoading(false);
        }
      };
      asyncFunc();
    }
  }, []);

  useEffect(() => {
    if (editItem) {
      setEditItem(null);
      setReadOnly(false);
    }
  }, [editItem]);

  const reset = useCallback(() => {
    setOpenDrawer(false);
    setEditItem(null);
    setReadOnly(false);
  }, []);

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Page Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.secondary.main} 60%, ${theme.palette.secondary.light} 100%)`,
          px: { xs: 2, sm: 4 },
          py: { xs: 2.5, sm: 3.5 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: 1,
          gap: 2,
          boxShadow: `0 4px 20px ${alpha(theme.palette.secondary.main, 0.4)}`,
        }}
      >
        <Stack direction="row" alignItems="center" gap={2}>
          <Box
            sx={{
              bgcolor: alpha("#fff", 0.15),
              borderRadius: 2,
              p: 1,
              display: "flex",
              alignItems: "center",
            }}
          >
            <PeopleAltIcon sx={{ color: "#fff", fontSize: { xs: 28, sm: 36 } }} />
          </Box>
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              fontWeight={700}
              sx={{ color: "#fff", lineHeight: 1.2 }}
            >
              Users
            </Typography>
            <Typography variant="body2" sx={{ color: alpha("#fff", 0.8), mt: 0.3 }}>
              {pathname === ROUTES.agent ? "Agent users" : "Jaman HP Gas — system users"}
            </Typography>
          </Box>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Chip
            label={`${list.length} users`}
            size="small"
            sx={{
              bgcolor: alpha("#fff", 0.2),
              color: "#fff",
              fontWeight: 600,
              fontSize: "0.7rem",
              display: { xs: "none", sm: "flex" },
            }}
          />
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => setOpenDrawer(true)}
            sx={{
              bgcolor: alpha("#fff", 0.2),
              color: "#fff",
              borderRadius: 2,
              fontWeight: 600,
              textTransform: "none",
              border: `1px solid ${alpha("#fff", 0.3)}`,
              flexShrink: 0,
              "&:hover": { bgcolor: alpha("#fff", 0.3) },
            }}
          >
            {isMobile ? "Add" : "Add User"}
          </Button>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ px: { xs: 1, sm: 3 }, pt: 2, pb: 4 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 10 }}>
            <CircularProgress color="secondary" />
          </Box>
        ) : (
          <Box
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              border: `1px solid ${theme.palette.divider}`,
              "& .MuiPaper-root": { boxShadow: "none !important" },
            }}
          >
            <DataGrid
              title="Jaman HP Gas - System Users"
              columns={columns}
              rows={list}
            />
          </Box>
        )}
      </Box>

      <Drawer
        title="User Details"
        variant="lg"
        anchor="right"
        open={openDrawer}
        onClose={() => reset()}
      >
        <UserForm
          readonly={readonly}
          editData={editItem}
          onParentClose={(data) => reset()}
        />
      </Drawer>
    </Box>
  );
};
